
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { Image, Icon } from 'semantic-ui-react';
import { useQuery } from '@apollo/client';
import { useState } from 'react';

import { formatDate } from '../../../../dates';
import { InterimStates } from '../../../../components/InterimStates';
import { UserPhoneNumber } from '../../../../components/UserPhoneNumber';
import { JoyyaHeader } from '../../../../components/JoyyaHeader';
import { GET_PERSON } from '../data/GET_PERSON';
import { CurrentBlockSection, CoursesSection, TeachingSection } from '../shared/ProfileSections';

import styles from './TeacherProfileView.module.css';

const TeacherProfileView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [editMode, setEditMode] = useState(false);

    const { loading, error, data } = useQuery(GET_PERSON, {
        variables: { personId: Number(id) },
        skip: !id || isNaN(Number(id)),
    });

    if (loading || error || !data?.user) {
        return <InterimStates loading={loading} error={error} label="person's details" />;
    }

    const { user } = data;
    if (user.id == null) return <div>You don't have permission to view this</div>;

    const personId = Number(id);

    return (
        <>
            <JoyyaHeader />
            <div className="mobile-frame">

                <div className={styles.overlay}>
                    <div className={styles.back} onClick={() => navigate(-1)}>
                        <Icon name="arrow left" size="large" style={{ verticalAlign: 'baseline' }} />
                        <span className={styles.backLabel}>Back</span>
                    </div>
                    <div className={styles.center}>
                        <Image src={user.photoUrl} size="large" circular className={styles.profileImage} />
                        <span className={styles.name}>{user.name}</span>
                        {(user.scriptName || user.preferredName) && (
                            <span className={styles.otherName}>
                                {user.scriptName} {user.preferredName && `(${user.preferredName})`}
                            </span>
                        )}
                        {!user.isBeingViewedByBuddy && (
                            <p className={styles.iden}>ID: {user.identifier}</p>
                        )}
                    </div>
                </div>

                {/* Key stats */}
                <div className={styles.statsRow}>
                    <div className={styles.statChip}>
                        <span className={styles.statValue}>{Math.round(user.attendancePercentage * 100)}%</span>
                        <span className={styles.statLabel}>Attendance</span>
                    </div>
                    <div className={styles.statChip}>
                        <span className={styles.statValue}>
                            {user.dob ? moment().diff(moment(user.dob), 'years') : '—'}
                        </span>
                        <span className={styles.statLabel}>Age (yrs)</span>
                    </div>
                    <div className={styles.statChip}>
                        <span className={styles.statValue}>{user.truency}</span>
                        <span className={styles.statLabel}>Truancy</span>
                    </div>
                </div>

                {/* Detail rows */}
                <div className={styles.detailList}>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Address</span>
                        <span className={styles.detailValue}>{user.address}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                            Phone
                            <span className={styles.edit} onClick={() => setEditMode(!editMode)}>
                                {editMode ? 'done' : 'edit'}
                            </span>
                        </span>
                        <span className={styles.detailValue}>
                            <UserPhoneNumber userId={user.id} inEditMode={editMode} />
                        </span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Date of Birth</span>
                        <span className={styles.detailValue}>{formatDate(user.dob)}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Picture</span>
                        <span className={styles.detailValue}>{user.picturePermission ? 'Allowed' : 'Not Allowed'}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Reg. Docs</span>
                        <span className={styles.detailValue}>
                            {user.haveRegistrationDocument ? 'Yes' : 'No'}
                            {user.registrationDocumentUrl && (
                                <> — <a target="_blank" href={user.registrationDocumentUrl} rel="noopener noreferrer">View</a></>
                            )}
                        </span>
                    </div>
                </div>

                <div className={styles.sectionCard}>
                    <div className={styles.sectionTitle}>Current Block Attendance</div>
                    <CurrentBlockSection
                        attendances={user.currentCourseAttendances}
                        personId={personId}
                        showSku={!user.isBeingViewedByBuddy}
                    />
                </div>

                {!user.isBeingViewedByBuddy && (
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionTitle}>Past Classes</div>
                        <CoursesSection courses={user.courses} personId={personId} />
                    </div>
                )}

                {user.coursesTeaching.length > 0 && (
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionTitle}>Classes Teaching</div>
                        <TeachingSection courses={user.coursesTeaching} />
                    </div>
                )}

            </div>
        </>
    );
};

export { TeacherProfileView };
